import { useEffect, useRef, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  size?: "sm" | "md" | "lg";
  /** Override the heading id, useful for tests. */
  titleId?: string;
}

const SIZE = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-2xl",
} as const;

/**
 * Lightweight, focus-trapping modal. Closes on Esc + backdrop click.
 *
 * Phase 1: enough for command palette / shortcut help / settings.
 * Phase 2 swap-in: Radix Dialog primitive once `@radix-ui` lands.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  size = "md",
  titleId,
}: ModalProps): JSX.Element | null {
  const ref = useRef<HTMLDivElement | null>(null);
  const heading = useRef<string>(titleId ?? `modal-title-${Math.random().toString(36).slice(2)}`);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const lastFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    lastFocus.current = (document.activeElement as HTMLElement | null) ?? null;
    // Push focus into the dialog
    const node = ref.current;
    if (node) {
      const target = node.querySelector<HTMLElement>(
        "[data-autofocus], input, textarea, select, button, [href], [tabindex]:not([tabindex='-1'])",
      );
      target?.focus();
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current();
      } else if (e.key === "Tab") {
        // simple focus trap
        if (!ref.current) return;
        const focusables = Array.from(
          ref.current.querySelectorAll<HTMLElement>(
            "input, textarea, select, button, [href], [tabindex]:not([tabindex='-1'])",
          ),
        ).filter((el) => !el.hasAttribute("disabled"));
        if (focusables.length === 0) { e.preventDefault(); ref.current.focus(); return; }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        if (e.shiftKey && (active === first || !ref.current.contains(active))) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && (active === last || !ref.current.contains(active))) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      lastFocus.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? heading.current : undefined}
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-fg-primary/30 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        tabIndex={-1}
        className={cn(
          "w-full bg-canvas rounded-lg shadow-lg border border-border-subtle",
          "max-h-[90vh] overflow-hidden flex flex-col",
          SIZE[size],
        )}
      >
        {title ? (
          <div className="px-5 py-4 border-b border-border-subtle">
            <h2 id={heading.current} className="text-lg font-bold text-fg-primary">
              {title}
            </h2>
            {description ? (
              <p className="text-sm text-fg-secondary mt-1">{description}</p>
            ) : null}
          </div>
        ) : null}
        <div className="overflow-auto p-5">{children}</div>
      </div>
    </div>
  );
}
